/**
 * FOC Requirements Tracker catalog.
 * Sections derived from the PRATUS Final Operating Capability Requirements (DRAFT) TOC.
 * Tours cover document section numbers (not FOC-* atomic IDs).
 */

import {
  FOC_TOUR_LABEL_CMD_IC,
  FOC_TOUR_LABEL_CMD_LEGAL,
  FOC_TOUR_LABEL_CMD_LIAISON,
  FOC_TOUR_LABEL_CMD_PIO,
  FOC_TOUR_LABEL_CMD_SAFETY,
  FOC_TOUR_LABEL_COP_LAYERS,
  FOC_TOUR_LABEL_FSLTP,
  FOC_TOUR_LABEL_HUB_COP,
  FOC_TOUR_LABEL_IAP_FORMS,
  FOC_TOUR_LABEL_IAP_PREP_OPS,
  FOC_TOUR_LABEL_INCIDENT_ROSTER,
  FOC_TOUR_LABEL_INCIDENTS,
  FOC_TOUR_LABEL_INITIAL_RESPONSE,
  FOC_TOUR_LABEL_NOTIFICATIONS,
  FOC_TOUR_LABEL_ORG_WORKSPACE,
  FOC_TOUR_LABEL_PLANNING,
  FOC_TOUR_LABEL_PLANNING_P_COMMAND_STAFF,
  FOC_TOUR_LABEL_PLANNING_P_MEETING,
  FOC_TOUR_LABEL_PLANNING_P_MODEL,
  FOC_TOUR_LABEL_PLANNING_P_PREP_TACTICS,
  FOC_TOUR_LABEL_PLANNING_P_TACTICS_MEETING,
  FOC_TOUR_LABEL_PLANNING_P_TACTICS_WORKFLOW,
  FOC_TOUR_LABEL_PLANNING_PREP_MEETING,
  FOC_TOUR_LABEL_AI_FOC,
  FOC_TOUR_LABEL_AI_IOC,
  FOC_TOUR_LABEL_DMOB,
  FOC_TOUR_LABEL_EXT,
  FOC_TOUR_LABEL_LOG,
  FOC_TOUR_LABEL_OPS,
  FOC_TOUR_LABEL_PLN_DEMOB,
  FOC_TOUR_LABEL_PLN_DOCS,
  FOC_TOUR_LABEL_PLN_PSC,
  FOC_TOUR_LABEL_PLN_RESOURCE,
  FOC_TOUR_LABEL_PLN_SITL,
  FOC_TOUR_LABEL_RES_211,
  FOC_TOUR_LABEL_RES_213RR,
  FOC_TOUR_LABEL_RES_214,
  FOC_TOUR_LABEL_RES_MODEL,
  FOC_TOUR_LABEL_SITL_COP,
  FOC_TOUR_LABEL_TRANSITION,
  FOC_TOUR_LABEL_WS,
} from 'components/MapPage/focTourLabels';
import {
  FOC_AI_FOC_TOUR_HREF,
  FOC_AI_IOC_TOUR_HREF,
  FOC_CMD_IC_TOUR_HREF,
  FOC_CMD_LEGAL_TOUR_HREF,
  FOC_CMD_LIAISON_TOUR_HREF,
  FOC_CMD_PIO_TOUR_HREF,
  FOC_CMD_SAFETY_TOUR_HREF,
  FOC_COP_LAYERS_TOUR_HREF,
  FOC_DMOB_TOUR_HREF,
  FOC_EXT_TOUR_HREF,
  FOC_FSLTP_TOUR_HREF,
  FOC_IAP_FORMS_TOUR_HREF,
  FOC_IAP_PREP_TOUR_HREF,
  FOC_INCIDENT_ROSTER_TOUR_HREF,
  FOC_INCIDENTS_TOUR_HREF,
  FOC_INITIAL_RESPONSE_TOUR_HREF,
  FOC_LOG_TOUR_HREF,
  FOC_NOTIFICATIONS_TOUR_HREF,
  FOC_OPS_TOUR_HREF,
  FOC_PLANNING_P_COMMAND_STAFF_TOUR_HREF,
  FOC_PLANNING_P_MEETING_TOUR_HREF,
  FOC_PLANNING_P_MODEL_TOUR_HREF,
  FOC_PLANNING_P_PREP_TACTICS_TOUR_HREF,
  FOC_PLANNING_P_TACTICS_MEETING_TOUR_HREF,
  FOC_PLANNING_P_TACTICS_WORKFLOW_TOUR_HREF,
  FOC_PLANNING_PREP_TOUR_HREF,
  FOC_PLANNING_TOUR_HREF,
  FOC_PLN_DEMOB_TOUR_HREF,
  FOC_PLN_DOCS_TOUR_HREF,
  FOC_PLN_PSC_TOUR_HREF,
  FOC_PLN_RESOURCE_TOUR_HREF,
  FOC_PLN_SITL_TOUR_HREF,
  FOC_RES_211_TOUR_HREF,
  FOC_RES_213RR_TOUR_HREF,
  FOC_RES_214_TOUR_HREF,
  FOC_RES_MODEL_TOUR_HREF,
  FOC_TRANSITION_TOUR_HREF,
  FOC_WS_TOUR_HREF,
  HUB_COP_TOUR_HREF,
  ORG_WORKSPACE_TOUR_HREF,
  SITL_COP_TOUR_HREF,
} from 'components/MapPage/focTourHrefs';

export type FocRequirementSection = {
  id: string;
  title: string;
};

/** Requirement sections 3.1–25.3 from the FOC Requirements TOC. */
export const FOC_REQUIREMENT_SECTIONS: readonly FocRequirementSection[] = [
  { id: '3.1', title: 'General User Profile' },
  { id: '3.2', title: 'Administrative User Functions' },
  { id: '3.3', title: 'Program Manager Functions' },
  { id: '3.4', title: 'Access Control and Permission Model' },
  { id: '4.1', title: 'Organizational Workspace Model' },
  { id: '4.2', title: 'Exercises, Simulation, and Preparedness Workspaces' },
  { id: '4.3', title: 'Planning Repository and Pre-Incident Planning' },
  {
    id: '4.3.1',
    title: 'Unit Incident Management Preparedness / Manage IMT',
  },
  { id: '4.4', title: 'Area Command and Multi-Incident Coordination' },
  { id: '5.1', title: 'Hub Operating Model' },
  { id: '5.2', title: 'Common Operating Picture — Map Data' },
  { id: '5.3', title: 'Incident-Level SITL COP Workspace' },
  { id: '5.4', title: 'Layers, Data, and Provenance' },
  { id: '5.5', title: 'Hierarchical COP and Geographic Navigation' },
  { id: '5.6', title: 'FSLTP and ArcGIS Online Integration' },
  { id: '6.1', title: 'Notification Management' },
  { id: '7.1', title: 'Incident Management' },
  { id: '7.2', title: 'Incident Roster' },
  { id: '8.1', title: 'Initial Response Operating Model' },
  { id: '8.2', title: 'Initial Response Workspace' },
  { id: '8.3', title: 'Initial Response Objectives and Actions' },
  { id: '8.4', title: 'Initial Response SITREP and Safety Functions' },
  { id: '9.1', title: 'Transition Requirement' },
  { id: '10.1', title: 'Planning P Operating Model' },
  { id: '10.2', title: 'Planning P Meeting Management' },
  { id: '10.3', title: 'Command and General Staff Meeting' },
  { id: '10.4', title: 'Prep for Tactics Workspace' },
  { id: '10.5', title: 'Tactics, Resource, and Safety Workflow' },
  { id: '10.6', title: 'Tactics Meeting' },
  { id: '11.1', title: 'Prep for Planning Meeting' },
  { id: '11.2', title: 'Planning Meeting' },
  { id: '12.1', title: 'IAP Component Tracking' },
  { id: '12.2', title: 'IAP Assembly and Approval' },
  { id: '12.3', title: 'Operations Brief' },
  { id: '12.4', title: 'ICS Form Lifecycle, Coverage, and Export Standard' },
  { id: '13.1', title: 'Common Workspace Requirement' },
  { id: '14.1', title: 'Incident Commander / Unified Command Workspace' },
  { id: '14.2', title: 'Safety Officer Workspace' },
  { id: '14.3', title: 'Liaison Officer Workspace' },
  { id: '14.4', title: 'Public Information Officer Workspace' },
  { id: '14.5', title: 'Legal Officer Workspace' },
  { id: '15.1', title: 'Planning Section Chief Workspace' },
  { id: '15.2', title: 'Situation Unit Workspace' },
  { id: '15.3', title: 'Resource Unit Workspace' },
  { id: '15.4', title: 'Documentation Unit Workspace' },
  { id: '15.5', title: 'Demobilization Unit Workspace' },
  { id: '16', title: 'Operations Section Workspace' },
  { id: '17.1', title: 'Persistent Logistics Capability' },
  { id: '18.1', title: 'Finance/Admin Operational Capability' },
  { id: '18.2', title: 'CG-5136-Compatible Cost Model' },
  { id: '18.3', title: 'Finance/Procurement Integration' },
  { id: '19', title: 'Intelligence and Investigations Section Workspace' },
  { id: '20.1', title: 'Resource Management Terminology and Model' },
  { id: '20.2', title: 'Resource Requests / ICS 213RR' },
  { id: '20.3', title: 'Check-In, Check-Out, and ICS 211' },
  { id: '20.4', title: 'ICS 214 Activity Log' },
  { id: '21', title: 'Demobilization' },
  { id: '22', title: 'External User Onboarding, Management, and Collaboration' },
  { id: '23.1', title: 'IOC Carryover Requirements' },
  { id: '23.2', title: 'FOC AI Requirements' },
  { id: '24', title: 'Mobile, Offline, and Edge Operations' },
  { id: '25.1', title: 'Security, Privacy, Accessibility, and Data Standards' },
  { id: '25.2', title: 'Capacity, Availability, and Recovery' },
  { id: '25.3', title: 'Remote, Web, Mobile, and Training Support' },
] as const;

export type FocTourSectionCover = {
  tourLabel: string;
  href: string;
  covers: readonly string[];
};

export type FocTourLink = {
  label: string;
  href: string;
};

/** Product tours → document sections they cover (+ hub tour deep links). */
export const FOC_TOUR_SECTION_COVERS: readonly FocTourSectionCover[] = [
  {
    tourLabel: FOC_TOUR_LABEL_ORG_WORKSPACE,
    href: ORG_WORKSPACE_TOUR_HREF,
    covers: ['4.1'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLANNING,
    href: FOC_PLANNING_TOUR_HREF,
    covers: ['4.3'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_HUB_COP,
    href: HUB_COP_TOUR_HREF,
    covers: ['5.1', '5.2'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_SITL_COP,
    href: SITL_COP_TOUR_HREF,
    covers: ['5.3'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_COP_LAYERS,
    href: FOC_COP_LAYERS_TOUR_HREF,
    covers: ['5.4', '5.5'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_FSLTP,
    href: FOC_FSLTP_TOUR_HREF,
    covers: ['5.6'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_NOTIFICATIONS,
    href: FOC_NOTIFICATIONS_TOUR_HREF,
    covers: ['6.1'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_INCIDENTS,
    href: FOC_INCIDENTS_TOUR_HREF,
    covers: ['7.1'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_INCIDENT_ROSTER,
    href: FOC_INCIDENT_ROSTER_TOUR_HREF,
    covers: ['7.2'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_INITIAL_RESPONSE,
    href: FOC_INITIAL_RESPONSE_TOUR_HREF,
    covers: ['8.1', '8.2', '8.3', '8.4'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_TRANSITION,
    href: FOC_TRANSITION_TOUR_HREF,
    covers: ['9.1'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLANNING_P_MODEL,
    href: FOC_PLANNING_P_MODEL_TOUR_HREF,
    covers: ['10.1'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLANNING_P_MEETING,
    href: FOC_PLANNING_P_MEETING_TOUR_HREF,
    covers: ['10.2'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLANNING_P_COMMAND_STAFF,
    href: FOC_PLANNING_P_COMMAND_STAFF_TOUR_HREF,
    covers: ['10.3'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLANNING_P_PREP_TACTICS,
    href: FOC_PLANNING_P_PREP_TACTICS_TOUR_HREF,
    covers: ['10.4'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLANNING_P_TACTICS_WORKFLOW,
    href: FOC_PLANNING_P_TACTICS_WORKFLOW_TOUR_HREF,
    covers: ['10.5'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLANNING_P_TACTICS_MEETING,
    href: FOC_PLANNING_P_TACTICS_MEETING_TOUR_HREF,
    covers: ['10.6'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLANNING_PREP_MEETING,
    href: FOC_PLANNING_PREP_TOUR_HREF,
    covers: ['11.1', '11.2'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_IAP_PREP_OPS,
    href: FOC_IAP_PREP_TOUR_HREF,
    covers: ['12.1', '12.2', '12.3'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_IAP_FORMS,
    href: FOC_IAP_FORMS_TOUR_HREF,
    covers: ['12.4'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_WS,
    href: FOC_WS_TOUR_HREF,
    covers: ['13.1'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_CMD_IC,
    href: FOC_CMD_IC_TOUR_HREF,
    covers: ['14.1'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_CMD_SAFETY,
    href: FOC_CMD_SAFETY_TOUR_HREF,
    covers: ['14.2'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_CMD_LIAISON,
    href: FOC_CMD_LIAISON_TOUR_HREF,
    covers: ['14.3'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_CMD_PIO,
    href: FOC_CMD_PIO_TOUR_HREF,
    covers: ['14.4'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_CMD_LEGAL,
    href: FOC_CMD_LEGAL_TOUR_HREF,
    covers: ['14.5'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLN_PSC,
    href: FOC_PLN_PSC_TOUR_HREF,
    covers: ['15.1'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLN_SITL,
    href: FOC_PLN_SITL_TOUR_HREF,
    covers: ['15.2'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLN_RESOURCE,
    href: FOC_PLN_RESOURCE_TOUR_HREF,
    covers: ['15.3'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLN_DOCS,
    href: FOC_PLN_DOCS_TOUR_HREF,
    covers: ['15.4'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_PLN_DEMOB,
    href: FOC_PLN_DEMOB_TOUR_HREF,
    covers: ['15.5'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_OPS,
    href: FOC_OPS_TOUR_HREF,
    covers: ['16'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_LOG,
    href: FOC_LOG_TOUR_HREF,
    covers: ['17.1'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_RES_MODEL,
    href: FOC_RES_MODEL_TOUR_HREF,
    covers: ['20.1'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_RES_213RR,
    href: FOC_RES_213RR_TOUR_HREF,
    covers: ['20.2'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_RES_211,
    href: FOC_RES_211_TOUR_HREF,
    covers: ['20.3'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_RES_214,
    href: FOC_RES_214_TOUR_HREF,
    covers: ['20.4'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_DMOB,
    href: FOC_DMOB_TOUR_HREF,
    covers: ['21'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_EXT,
    href: FOC_EXT_TOUR_HREF,
    covers: ['22'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_AI_IOC,
    href: FOC_AI_IOC_TOUR_HREF,
    covers: ['23.1'],
  },
  {
    tourLabel: FOC_TOUR_LABEL_AI_FOC,
    href: FOC_AI_FOC_TOUR_HREF,
    covers: ['23.2'],
  },
] as const;

/** All FOC tour labels (for completeness checks). */
export const ALL_FOC_TOUR_LABELS: readonly string[] =
  FOC_TOUR_SECTION_COVERS.map((entry) => entry.tourLabel);

export type FocCoverageRow = {
  id: string;
  title: string;
  covered: boolean;
  tourLabels: string[];
  tours: FocTourLink[];
};

export type FocTourCoverage = {
  total: number;
  covered: number;
  missing: number;
  percent: number;
  rows: FocCoverageRow[];
};

export function computeFocTourCoverage(
  sections: readonly FocRequirementSection[] = FOC_REQUIREMENT_SECTIONS,
  tourCovers: readonly FocTourSectionCover[] = FOC_TOUR_SECTION_COVERS
): FocTourCoverage {
  const sectionToTours = new Map<string, FocTourLink[]>();
  for (const tour of tourCovers) {
    for (const sectionId of tour.covers) {
      const list = sectionToTours.get(sectionId) ?? [];
      list.push({ label: tour.tourLabel, href: tour.href });
      sectionToTours.set(sectionId, list);
    }
  }

  const rows: FocCoverageRow[] = sections.map((section) => {
    const tours = sectionToTours.get(section.id) ?? [];
    return {
      id: section.id,
      title: section.title,
      covered: tours.length > 0,
      tourLabels: tours.map((tour) => tour.label),
      tours,
    };
  });

  const covered = rows.filter((row) => row.covered).length;
  const total = rows.length;
  const percent = total === 0 ? 0 : Math.round((covered / total) * 100);

  return {
    total,
    covered,
    missing: total - covered,
    percent,
    rows,
  };
}
